import React, { useRef, useEffect, useState } from 'react'
import { normPixelToCanvas, normValueToPixel } from '../../Utils/PixelUtils'
import ActionTypes from '../../Models/ActionTypes';
import Consts from '../../Constants'
import getDrawFunc from './DrawingStrategies'



function drawBrushPreview(data, context) {
    let { x, y } = normPixelToCanvas(data.brushPreview.pos.x, data.brushPreview.pos.y, context.canvas.width);
    const diameter = normValueToPixel(data.brushPreview.strokeSize, context.canvas.width);
    context.fillStyle = data.brushPreview.color;
    context.strokeStyle = '#FFFFFF';
    context.lineWidth = 1;
    if(data.brushPreview.color.toUpperCase() === '#FFFFFF') {
        context.strokeStyle = '#000000';
    }
    context.beginPath();
    context.arc(x, y, diameter / 2, 0, 2 * Math.PI);
    context.fill();
    context.arc(x, y, diameter / 2, 0, 2 * Math.PI);
    context.stroke();
}

const Canvas = props => {

    var { data, onTouchStart, onTouchEnd, onTouchMove, onScroll, width, height, border, redraw, hideCursor, drawingStrategy, ...other } = props
    height = width * Consts.canvasAspectRatio

    const backgroundCanvasRef = useRef(null)
    const markerCanvasRef = useRef(null)
    const markerPreviewCanvasRef = useRef(null)
    const penCanvasRef = useRef(null)
    const penPreviewCanvasRef = useRef(null)
    const containerRef = useRef(null)


    const [lastRedraw, setLastRedraw] = useState(0)
    const [lastPenData, setLastPenData] = useState({ paths: 0, latestPath: 0 })
    const [lastMarkerData, setLastMarkerData] = useState({ paths: 0, latestPath: 0 })


    useEffect(() => {
        setLastRedraw(0)
    }, [penCanvasRef.current, window.devicePixelRatio]);

    useEffect(() => {
        setLastRedraw(0)
    }, [markerCanvasRef.current, window.devicePixelRatio]);

    useEffect(() => {
        var newPenPaths = data.pen.paths
        var newMarkerPaths = data.marker.paths
        if (lastRedraw < props.redraw) {
            setLastRedraw(Date.now())
            setLastPenData({
                paths: data.pen.paths.length,
                latestPath: data.pen.paths.length > 0 ? data.pen.paths[data.pen.paths.length - 1].length : 0
            })
            setLastMarkerData({
                paths: data.marker.paths.length,
                latestPath: data.marker.paths.length > 0 ? data.marker.paths[data.marker.paths.length - 1].length : 0
            })
            const penContext = penCanvasRef.current.getContext('2d')
            const markerContext = markerCanvasRef.current.getContext('2d')
            penContext.clearRect(0, 0, penContext.canvas.width, penContext.canvas.height);
            markerContext.clearRect(0, 0, markerContext.canvas.width, markerContext.canvas.height);
        } else {
            newPenPaths = newPenPaths.slice(lastPenData.paths - 1)
            newPenPaths = JSON.parse(JSON.stringify(newPenPaths)) // break reference
            if (newPenPaths[0] && newPenPaths[0].points.length > lastPenData.latestPath) {
                newPenPaths[0].points = newPenPaths[0].points.slice(Math.max(0, lastPenData.latestPath - 1))
            } else if (newPenPaths[0]) {
                newPenPaths[0].points = []
            }
            setLastPenData({
                paths: data.pen.paths.length,
                latestPath: data.pen.paths.length > 0 ? data.pen.paths[data.pen.paths.length - 1].points.length : 0
            })

            newMarkerPaths = newMarkerPaths.slice(lastMarkerData.paths - 1)
            newMarkerPaths = JSON.parse(JSON.stringify(newMarkerPaths)) // break reference
            if (newMarkerPaths[0] && newMarkerPaths[0].points.length > lastMarkerData.latestPath) {
                newMarkerPaths[0].points = newMarkerPaths[0].points.slice(Math.max(0, lastMarkerData.latestPath - 1))
            } else if (newMarkerPaths[0]) {
                newMarkerPaths[0].points = []
            }
            setLastMarkerData({
                paths: data.marker.paths.length,
                latestPath: data.marker.paths.length > 0 ? data.marker.paths[data.marker.paths.length - 1].points.length : 0
            })
        }


        const penContext = penCanvasRef.current.getContext('2d')
        const markerContext = markerCanvasRef.current.getContext('2d')
        const penPreviewContext = penPreviewCanvasRef.current.getContext('2d')
        const markerPreviewContext = markerPreviewCanvasRef.current.getContext('2d')

        penContext.lineCap = 'round';
        markerContext.lineCap = 'round';
        penPreviewContext.lineCap = 'round';
        markerPreviewContext.lineCap = 'round';


        penPreviewContext.clearRect(0, 0, penPreviewContext.canvas.width, penPreviewContext.canvas.height);
        markerPreviewContext.clearRect(0, 0, markerPreviewContext.canvas.width, markerPreviewContext.canvas.height);

        const drawFunc = getDrawFunc(drawingStrategy);

        drawFunc(data.marker.paths, newMarkerPaths, markerContext);
        drawFunc(data.pen.paths, newPenPaths, penContext);

        if (data.brushPreview && data.brushPreview.type === ActionTypes.MARKER) {
            drawBrushPreview(data, markerPreviewContext);
        }
        if (data.brushPreview && data.brushPreview.type === ActionTypes.PEN) {
            drawBrushPreview(data, penPreviewContext);
        }
    }, [data, width, redraw, drawingStrategy, lastRedraw]);

    useEffect(() => {
        const backgroundContext = backgroundCanvasRef.current.getContext('2d')
        const penContext = penCanvasRef.current.getContext('2d')
        const markerContext = markerCanvasRef.current.getContext('2d')
        const penPreviewContext = penPreviewCanvasRef.current.getContext('2d')
        const markerPreviewContext = markerPreviewCanvasRef.current.getContext('2d')


        backgroundContext.lineCap = 'round';
        penContext.lineCap = 'round';
        markerContext.lineCap = 'round';
        penPreviewContext.lineCap = 'round';
        markerPreviewContext.lineCap = 'round';

        if (data.background) {
            backgroundContext.fillStyle = data.background;
            backgroundContext.fillRect(0, 0, backgroundContext.canvas.width, backgroundContext.canvas.height);

            const drawFunc = getDrawFunc(drawingStrategy);

            for (var existing of data.existing ?? []) {
                var markerPaths = existing.marker.paths ?? [];
                var penPaths = existing.pen.paths ?? [];
                drawFunc(markerPaths, markerPaths, backgroundContext);
                drawFunc(penPaths, penPaths, backgroundContext);
            }
        }
    }, [backgroundCanvasRef, penCanvasRef, markerCanvasRef, penPreviewCanvasRef, markerPreviewCanvasRef, data.background, data.existing, drawingStrategy, window.devicePixelRatio])

    useEffect(() => {
        const container = containerRef.current;
        const emptyFunc = () => { };

        const onstart = onTouchStart ? onTouchStart : emptyFunc
        const onend = onTouchEnd ? onTouchEnd : emptyFunc
        const onmove = onTouchMove ? onTouchMove : emptyFunc
        const onscroll = onScroll ? onScroll : emptyFunc

        container.addEventListener('touchstart', onstart, { passive: false });
        container.addEventListener('touchend', onend, { passive: false });
        container.addEventListener('touchmove', onmove, { passive: false });
        container.addEventListener('wheel', onscroll, { passive: false });
        return function cleanup() {
            container.removeEventListener('touchstart', onstart);
            container.removeEventListener('touchend', onend);
            container.removeEventListener('touchmove', onmove);
            container.removeEventListener('wheel', onscroll);
        };
    }, [backgroundCanvasRef, onTouchStart, onTouchEnd, onTouchMove, onScroll]);

    const divStyle = {
        border: border,
        position: "relative",
        width: width,
        height: height
    }
    if (hideCursor !== false) {
        divStyle.cursor = "none"
    }

    const canvasStyle = {
        position: "absolute",
        top: 0,
        left: 0,
        width: width,
        height: height
    }

    const canvasWidth = width * window.devicePixelRatio;
    const canvasHeight = height * window.devicePixelRatio;

    return <div style={divStyle} ref={containerRef}>
        <canvas ref={backgroundCanvasRef} width={canvasWidth} height={canvasHeight} {...other} style={canvasStyle} />
        <canvas ref={markerCanvasRef} width={canvasWidth} height={canvasHeight} {...other} style={canvasStyle} />
        <canvas ref={markerPreviewCanvasRef} width={canvasWidth} height={canvasHeight} {...other} style={canvasStyle} />
        <canvas ref={penCanvasRef} width={canvasWidth} height={canvasHeight} {...other} style={canvasStyle} />
        <canvas ref={penPreviewCanvasRef} width={canvasWidth} height={canvasHeight} {...other} style={canvasStyle} />
    </div>
}

export default Canvas
